import {
  SANS_2,
  ButtonUpload,
  colors,
  Divider,
  ButtonGhostCompact,
  useBannerContext,
  DropdownSingle,
  PaddingTop15,
  TextInput,
  colors2,
  UI_CAPTION_DF,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION_SEMIBOLD_DF,
} from "oolib";
import React, { useState } from "react";
import { parseExcel } from "../../utils";
import { LAYOUT_CONSTANTS } from "@/_self_managed/LAYOUT_CONSTANTS";

const { greyColor15 } = colors;

export const UploadButtonAndPreview = ({
  file: fileFromParent,
  setFile,
  setSelectedSheetName,
  selectedSheetName,
  onFileSelect,
  customField = null,
  allowedFormats = ["xls", "csv"],
}) => {
  if (!allowedFormats || allowedFormats.length === 0) {
    throw new Error(
      "UploadButtonAndPreview needs at least 1 allowed file format to work"
    );
  }

  const { SET_ALERT_BANNER } = useBannerContext();
  const [sheetNames, setSheetNames] = useState(undefined);
  const [isDragOver, setIsDragOver] = useState(false);

  const allowsCsv = allowedFormats.includes("csv");
  const allowsXls = allowedFormats.includes("xls");

  const getSheetNamesAndSetFile = async (file) => {
    try {
      if (!file) return;

      // For CSV files, skip sheet name extraction
      const isCsvFile = file.type === "text/csv" || file.name.endsWith(".csv");

      if (isCsvFile && allowsCsv) {
        
        setFile && setFile(file);
        setSelectedSheetName && setSelectedSheetName(null); // CSV doesn't have sheets
        
        return;
      }

      // For Excel files
      let { sheetNames } = await parseExcel(file);
      if (!sheetNames || sheetNames.length === 0)
        throw new Error("Excel has no sheets");

        setSheetNames(sheetNames.map((d) => ({ display: d, value: d })));
        setFile && setFile(file);
        setSelectedSheetName && setSelectedSheetName(sheetNames[0]);
      
    } catch (err) {
      SET_ALERT_BANNER(err.msg, "red", 4000);
    }
  };

  const handleFileSelect = (file) => {
    const isExcelFile =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls");

    const isCsvFile = file.type === "text/csv" || file.name.endsWith(".csv");

    const isValidFormat =
      (allowsXls && isExcelFile) || (allowsCsv && isCsvFile);

    if (!isValidFormat) {
      const formats = [];
      if (allowsCsv) formats.push(".csv"); // CSV first
      if (allowsXls) formats.push(".xlsx or .xls");
      SET_ALERT_BANNER(
        `Please upload ${formats.join(" or ")} file`,
        "red",
        4000
      );
      return;
    }

    if(onFileSelect){
      onFileSelect(file)
    }else{
      getSheetNamesAndSetFile(file);
    }
    
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUploadChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Generate accept attribute based on allowed formats
  const getAcceptAttribute = () => {
    const accepts = [];
    if (allowsCsv) {
      // CSV first
      accepts.push(".csv", "text/csv");
    }
    if (allowsXls) {
      accepts.push(
        ".xlsx",
        ".xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel"
      );
    }
    return accepts.join(",");
  };

  const getDropZoneText = () => {
    if (allowsXls && allowsCsv) return "CSV or Excel file";
    if (allowsCsv) return "CSV file";
    return "Excel file";
  };

  return (
    <div>
      {!fileFromParent ? (
        <div
          style={{
            border: `1px dashed ${isDragOver ? colors2.blue : colors2.grey50}`,
            borderRadius: "8px",
            padding: "6rem 2rem",
            textAlign: "center",
            backgroundColor: isDragOver ? "#f8f9fa" : "transparent",
            transition: "all 0.2s ease",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            gap: LAYOUT_CONSTANTS.topBottomPadding + "px",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <ButtonUpload
              S
              id={"batchRegisterUploadBtn"}
              multiple={false}
              onChange={handleUploadChange}
              accept={getAcceptAttribute()}
              children={`Upload ${getDropZoneText()}`}
            />
          </div>
          <div>
            <UI_CAPTION_SEMIBOLD_DF
              style={{ color: isDragOver ? colors2.blue : colors2.grey50 }}
            >
              {isDragOver
                ? `Drop your ${getDropZoneText()} here`
                : `Drag and drop a ${getDropZoneText()} here, or`}
            </UI_CAPTION_SEMIBOLD_DF>
          </div>
          
        </div>
      ) : (
        <div>
          <div
            style={{
              padding: "1rem 2rem",
              border: `1px solid ${greyColor15}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              columnGap: "1rem",
            }}
          >
            <SANS_2>{`Uploaded File: ${fileFromParent.name}`}</SANS_2>
            <ButtonGhostCompact
              
              icon="Trash"
              onClick={() => {
                setFile && setFile(undefined);
                setSheetNames(undefined);
                setSelectedSheetName && setSelectedSheetName(undefined);
              }}
            />
          </div>
          <div style={{ margin: "1rem 0 1rem 0" }}>
            <Divider />
          </div>
          {sheetNames && (
            <DropdownSingle
              saveValueAsString
              options={sheetNames}
              placeholder="Select A Sheet"
              value={selectedSheetName}
              onChange={(k, v) => setSelectedSheetName && setSelectedSheetName(v)}
            />
          )}
          {customField}
        </div>
      )}
    </div>
  );
};
